import { memo, useMemo } from 'react';

import { TFunction } from 'i18next';
import {
  ArrowRightIcon,
  CheckIcon,
  ClockIcon,
  GoalIcon,
  MailIcon,
  MessageSquareIcon,
  OctagonAlertIcon,
  PhoneIcon,
  PhoneOffIcon,
  PlayIcon,
  SendIcon,
  XIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CampaignSubscriberWithJourney, JourneySession } from '@/shared/types/campaigns';
import { getChannelDisplayName } from '@/util/channelName';
import {
  DATE_BUCKET_LABEL_KEY,
  DateBucket,
  formatSecondsToReadable,
  getDateBucket,
  getSecondsDifference,
  parseTimestamp,
  timeFromNowShort,
} from '@/util/dateUtils';

import { TraceExpander } from './TraceExpander';
import { getStopTitleKey, SubscriberStopInfo } from '../../util/getSubscriberStopInfo';

export interface TimelineEvent {
  type:
    | 'session_sent'
    | 'session_answered'
    | 'session_voicemail'
    | 'session_failed'
    | 'follow_up_scheduled'
    | 'follow_up_now'
    | 'stop'
    | 'nba_failed'
    | 'campaign_started'
    | 'contact_added';
  label: string;
  /** Optional secondary detail rendered as ` · <detail>` after the label (e.g. the stop block name). */
  detail?: string;
  /** Optional value (email, phone) rendered semi-bold after the label. */
  value?: string;
  timestamp: number;
  sessionId?: string;
  channel?: string;
  /** Free-text NBA reason, rendered behind an expandable "View trace" toggle. */
  reason?: string;
}

interface ActivityTimelineProps {
  /** Journey plus the row timestamps/contact fields used to synthesize start/stop/contact events. */
  subscriber: CampaignSubscriberWithJourney;
  contactName?: string;
  onSessionClick?: (sessionId: string) => void;
  /** Pre-computed stop info from `getSubscriberStopInfo`. When set, the stop event is enriched with the block-code label. */
  stopInfo?: SubscriberStopInfo | null;
}

export const isVoiceChannel = (channel?: string) =>
  channel === 'voice' ||
  channel === 'twilio-voice' ||
  channel === 'telnyx-voice' ||
  channel === 'agora-voice';

const getChannelIcon = (channel?: string) => {
  switch (channel) {
    case 'whatsapp':
      return MessageSquareIcon;
    case 'email':
      return MailIcon;
    case 'voice':
    case 'twilio-voice':
    case 'telnyx-voice':
      return PhoneIcon;
    default:
      return SendIcon;
  }
};

export const getEventIcon = (event: TimelineEvent) => {
  switch (event.type) {
    case 'session_sent':
      return getChannelIcon(event.channel);
    case 'session_answered':
      return isVoiceChannel(event.channel) ? PhoneIcon : CheckIcon;
    case 'session_voicemail':
      return PhoneIcon;
    case 'session_failed':
      return isVoiceChannel(event.channel) ? PhoneOffIcon : XIcon;
    case 'follow_up_scheduled':
      return ClockIcon;
    case 'follow_up_now':
      return ArrowRightIcon;
    case 'stop':
      return OctagonAlertIcon;
    case 'nba_failed':
      return XIcon;
    case 'campaign_started':
      return GoalIcon;
    case 'contact_added':
      // Email values contain '@'; phone numbers don't.
      return event.value?.includes('@') ? MailIcon : PhoneIcon;
  }
};

/** Icon color + soft circular bubble background per event type (see Figma 5581-7705). */
const EVENT_STYLE: Record<TimelineEvent['type'], { icon: string; bubble: string }> = {
  session_sent: { icon: 'text-informational', bubble: 'bg-bg-info-soft' },
  session_answered: { icon: 'text-successful', bubble: 'bg-bg-success-soft' },
  session_voicemail: { icon: 'text-destructive', bubble: 'bg-bg-destructive-soft' },
  session_failed: { icon: 'text-destructive', bubble: 'bg-bg-destructive-soft' },
  follow_up_scheduled: { icon: 'text-informational', bubble: 'bg-bg-info-soft' },
  follow_up_now: { icon: 'text-informational', bubble: 'bg-bg-info-soft' },
  stop: { icon: 'text-destructive', bubble: 'bg-bg-destructive-soft' },
  nba_failed: { icon: 'text-destructive', bubble: 'bg-bg-destructive-soft' },
  campaign_started: { icon: 'text-successful', bubble: 'bg-bg-success-soft' },
  contact_added: { icon: 'text-foreground-muted', bubble: 'bg-background' },
};

/**
 * Maps a failure category from `NBAOutput.error_type` to a friendly, PII-safe detail line.
 * Static translation keys only (see conventions). Returns undefined for unknown/absent types so
 * the timeline falls back to the generic `nba_failed` label — which is the common case until the
 * NBA service threads `error_type` through (MOV-16650).
 */
const getNbaFailureDetail = (errorType: string | undefined, t: TFunction): string | undefined => {
  switch (errorType) {
    case 'TimeoutError':
      return t('campaigns.subscriber_journey.nba_failed_reason.timeout');
    case 'DecisionLayerError':
      return t('campaigns.subscriber_journey.nba_failed_reason.decision_error');
    case 'MaxRetriesExceeded':
      return t('campaigns.subscriber_journey.nba_failed_reason.max_retries');
    default:
      return undefined;
  }
};

/**
 * Maps a voice outcome code on the channel receipt to a label.
 * Static translation keys only (see conventions).
 */
const getVoiceOutcomeLabel = (statusCode: string | undefined, t: TFunction): string | undefined => {
  switch (statusCode) {
    case 'no_answer':
      return t('campaigns.subscriber_journey.call_not_answered');
    case 'busy_or_rejected':
      return t('campaigns.subscriber_journey.call_busy_or_declined');
    case 'rejected':
      return t('campaigns.subscriber_journey.call_declined');
    case 'invalid_number':
    case 'unresolvable_destination':
      return t('campaigns.subscriber_journey.call_invalid_number');
    default:
      return undefined;
  }
};

const getCallDurationDetail = (session: JourneySession): string | undefined => {
  if (!session.ended_at) {
    return undefined;
  }
  const seconds = getSecondsDifference(session.ended_at, session.started_at);
  return seconds > 0 ? formatSecondsToReadable(seconds) : undefined;
};

export const sessionToEvent = (
  session: JourneySession,
  t: TFunction,
  contactName?: string
): TimelineEvent | null => {
  const sentAt = session.sent_at || session.started_at;
  if (!sentAt) {
    return null;
  }

  const channelName = getChannelDisplayName(session.channel);

  // Show the most significant outcome as a single event — voicemail outranks `replied_at`.
  if (session.has_voicemail) {
    return {
      type: 'session_voicemail',
      label: t('campaigns.subscriber_journey.voicemail_answered_call'),
      detail: getCallDurationDetail(session),
      timestamp: sentAt,
      channel: session.channel,
      sessionId: session.session_id,
    };
  }

  if (session.replied_at != null) {
    const name = contactName || t('campaigns.subscriber_journey.contact');
    const base = {
      timestamp: session.replied_at,
      channel: session.channel,
      sessionId: session.session_id,
    };
    if (!isVoiceChannel(session.channel)) {
      return {
        ...base,
        type: 'session_answered',
        label: t('campaigns.subscriber_journey.contact_replied', { 0: name }),
      };
    }
    return {
      ...base,
      type: 'session_answered',
      label: t('campaigns.subscriber_journey.contact_answered_call', { 0: name }),
      detail: getCallDurationDetail(session),
    };
  }

  if (session.failed_at != null) {
    const label = isVoiceChannel(session.channel)
      ? (getVoiceOutcomeLabel(session.status_code, t) ??
        t('campaigns.subscriber_journey.call_not_placed'))
      : t('campaigns.subscriber_journey.message_not_delivered', { 0: channelName });
    return {
      type: 'session_failed',
      label,
      timestamp: sentAt,
      channel: session.channel,
      sessionId: session.session_id,
    };
  }

  // For voice, Delivered only means the signalling reached the handset — an
  // outcome code with no reply is a call nobody took, not a call in progress.
  const unansweredLabel = isVoiceChannel(session.channel)
    ? getVoiceOutcomeLabel(session.status_code, t)
    : undefined;
  if (unansweredLabel) {
    return {
      type: 'session_failed',
      label: unansweredLabel,
      timestamp: sentAt,
      channel: session.channel,
      sessionId: session.session_id,
    };
  }

  const label = isVoiceChannel(session.channel)
    ? t('campaigns.subscriber_journey.agent_called_via', { 0: channelName })
    : t('campaigns.subscriber_journey.agent_sent_message', { 0: channelName });
  return {
    type: 'session_sent',
    label,
    timestamp: sentAt,
    channel: session.channel,
    sessionId: session.session_id,
  };
};

const buildStopDetail = (info: SubscriberStopInfo, t: TFunction): string =>
  t(getStopTitleKey(info));

/** Builds the newest-first timeline from the journey plus synthesized stop/start/contact events. */
export const buildTimelineEvents = (
  subscriber: CampaignSubscriberWithJourney,
  stopInfo: SubscriberStopInfo | null | undefined,
  t: TFunction,
  contactName?: string
): TimelineEvent[] => {
  const allEvents: TimelineEvent[] = [];
  let hasJourneyStop = false;

  for (const entry of subscriber.contact_journey ?? []) {
    if (entry.session) {
      const event = sessionToEvent(entry.session, t, contactName);
      if (event) {
        allEvents.push(event);
      }
    } else if (entry.nba_output) {
      const { action, timestamp, status, error_type } = entry.nba_output;
      if (status === 'failed') {
        allEvents.push({
          type: 'nba_failed',
          label: t('campaigns.subscriber_journey.nba_failed'),
          detail: getNbaFailureDetail(error_type, t),
          timestamp,
        });
        continue;
      }
      if (!action) {
        continue;
      }
      if (action.label === 'follow_up_later') {
        allEvents.push({
          type: 'follow_up_scheduled',
          label: t('campaigns.subscriber_journey.agent_scheduled_follow_up'),
          timestamp,
          reason: action.reason,
        });
      } else if (action.label === 'follow_up_now') {
        const channelName = action.channel ? getChannelDisplayName(action.channel) : undefined;
        allEvents.push({
          type: 'follow_up_now',
          label: channelName
            ? t('campaigns.subscriber_journey.agent_initiated_follow_up', { 0: channelName })
            : t('campaigns.subscriber_journey.agent_initiated_follow_up_generic'),
          timestamp,
          channel: action.channel,
          reason: action.reason,
        });
      } else if (action.label === 'stop') {
        const isCurrentStop = stopInfo?.source === 'nba_stop' && stopInfo.stoppedAt === timestamp;
        hasJourneyStop ||= isCurrentStop;
        allEvents.push({
          type: 'stop',
          label: t('campaigns.subscriber_journey.follow_up_stopped'),
          detail: isCurrentStop ? buildStopDetail(stopInfo, t) : undefined,
          timestamp,
          reason: action.reason,
        });
      }
    }
  }

  // Synthesize a stop when the current stop is not an NBA stop event in the journey (manual/lifecycle stop).
  if (!hasJourneyStop && stopInfo) {
    const updatedTimestamp = subscriber.updated
      ? (parseTimestamp(subscriber.updated)?.getTime() ?? NaN)
      : NaN;
    const fallbackTimestamp = stopInfo.stoppedAt ?? updatedTimestamp;
    if (Number.isFinite(fallbackTimestamp)) {
      allEvents.push({
        type: 'stop',
        label: t('campaigns.subscriber_journey.stopped'),
        detail: buildStopDetail(stopInfo, t),
        timestamp: fallbackTimestamp,
      });
    }
  }

  // All anchored at subscriber creation; push campaign_started last so the stable sort keeps it at the bottom.
  const createdTimestamp = subscriber.created
    ? (parseTimestamp(subscriber.created)?.getTime() ?? NaN)
    : NaN;
  if (Number.isFinite(createdTimestamp)) {
    if (subscriber.phone) {
      allEvents.push({
        type: 'contact_added',
        label: t('campaigns.subscriber_journey.phone_added'),
        value: subscriber.phone,
        timestamp: createdTimestamp,
      });
    }
    if (subscriber.email) {
      allEvents.push({
        type: 'contact_added',
        label: t('campaigns.subscriber_journey.email_added'),
        value: subscriber.email,
        timestamp: createdTimestamp,
      });
    }
    allEvents.push({
      type: 'campaign_started',
      label: t('campaigns.subscriber_journey.campaign_started'),
      timestamp: createdTimestamp,
    });
  }

  return allEvents.sort((a, b) => b.timestamp - a.timestamp);
};

const ActivityRow = memo(function ActivityRow({
  event,
  onSessionClick,
}: {
  event: TimelineEvent;
  onSessionClick?: (sessionId: string) => void;
}) {
  const { t } = useTranslation();
  const Icon = getEventIcon(event);
  const style = EVENT_STYLE[event.type];
  const hasReplay = !!event.sessionId && !!onSessionClick;

  return (
    <div className="flex gap-2 px-1 py-1.5 text-sm">
      <div className="w-16 shrink-0 pt-0.5">
        <TimestampTooltip timestamp={event.timestamp}>
          <span className="text-foreground-muted">{timeFromNowShort(event.timestamp)}</span>
        </TimestampTooltip>
      </div>
      <div className="flex min-w-0 flex-1 flex-col items-start justify-center gap-2">
        <div className="flex items-center gap-2">
          <div
            className={cn(
              'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
              style.bubble
            )}
          >
            <Icon size={16} className={style.icon} />
          </div>
          <span className="text-base leading-6 font-normal text-foreground">
            {event.label}
            {event.value && (
              <>
                {' '}
                <span className="font-semibold">{event.value}</span>
              </>
            )}
            {event.detail && (
              <>
                {' · '}
                {event.detail}
              </>
            )}
            {hasReplay && (
              <InfoTooltip content={t('common.preview')}>
                <Button
                  variant="ghost-neutral"
                  size="icon-sm"
                  className="ml-1 inline-flex h-5 w-5 align-text-bottom"
                  onClick={() => onSessionClick!(event.sessionId!)}
                  aria-label={t('common.preview')}
                >
                  <PlayIcon size={12} />
                </Button>
              </InfoTooltip>
            )}
          </span>
        </div>
        {event.reason && (
          <TraceExpander>
            <p className="max-w-2xl min-w-0 text-sm leading-5 font-normal break-words whitespace-pre-wrap text-foreground-muted">
              {event.reason}
            </p>
            <span className="shrink-0">
              <TimestampTooltip timestamp={event.timestamp}>
                <span className="text-sm leading-5 font-normal whitespace-nowrap text-foreground-muted">
                  {timeFromNowShort(event.timestamp)}
                </span>
              </TimestampTooltip>
            </span>
          </TraceExpander>
        )}
      </div>
    </div>
  );
});

export const ActivityTimeline = ({
  subscriber,
  contactName,
  onSessionClick,
  stopInfo,
}: ActivityTimelineProps) => {
  const { t } = useTranslation();

  const events = useMemo(
    () => buildTimelineEvents(subscriber, stopInfo, t, contactName),
    [subscriber, contactName, stopInfo, t]
  );

  // Events are sorted newest-first, so consecutive runs map 1:1 to date buckets.
  const groups = useMemo(() => {
    const buckets: { key: DateBucket; events: TimelineEvent[] }[] = [];
    const now = Date.now();
    for (const event of events) {
      const bucket = getDateBucket(event.timestamp, now);
      const lastGroup = buckets[buckets.length - 1];
      if (lastGroup?.key === bucket) {
        lastGroup.events.push(event);
      } else {
        buckets.push({ key: bucket, events: [event] });
      }
    }
    return buckets;
  }, [events]);

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">{t('campaigns.subscriber_journey.activity')}</h2>
      {events.length === 0 ? (
        <p className="text-sm text-foreground-muted">
          {t('campaigns.subscriber_journey.no_activity')}
        </p>
      ) : (
        <>
          {groups.map((group) => (
            <div key={group.key}>
              <div className="flex items-center gap-1 py-2">
                <div className="h-px flex-1 bg-stroke" />
                <Badge
                  type="outline"
                  flavour="neutral"
                  rounded={false}
                  className="border-border bg-background"
                >
                  {t(DATE_BUCKET_LABEL_KEY[group.key])}
                </Badge>
                <div className="h-px flex-1 bg-stroke" />
              </div>
              <div className="space-y-0.5">
                {group.events.map((event, index) => {
                  const key = event.sessionId
                    ? `${event.type}-${event.sessionId}`
                    : `${event.type}-${event.timestamp}-${event.label}-${index}`;
                  return <ActivityRow key={key} event={event} onSessionClick={onSessionClick} />;
                })}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};
